import {
  computed,
  DestroyRef,
  EnvironmentProviders,
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
  Type,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {mapResponse} from '@ngrx/operators';
import {signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {Dispatcher, EventCreator, EventInstance, Events, withEventHandlers} from '@ngrx/signals/events';
import {updateState, withGlitchTracking} from '@oleksandroleniuk/ngrx-toolkit';
import {ErrorService, getCommandFailureText, IErrorModel} from '@scalified-monorepo/ng-shared/errors';
import {
  AppErrorEvent,
  CqrsCommandErrorType,
  CqrsCommandFactory,
  CqrsCommandSuccessFactory,
  CqrsQueryErrorType,
  CqrsQueryFactory,
  CqrsQuerySuccessFactory,
  CqrsQuerySuccessWithContextFactory,
  eventsForTypes,
  withDevTools,
} from '@scalified-monorepo/ng-shared/ngrx';
import {
  CqrsCommand,
  CqrsEvent,
  COMMAND_EXECUTION_FAILED_EVENT_TYPE,
  CqrsQuery,
  IMessage,
  newId,
} from '@scalified-monorepo/shared';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  mergeMap,
  Observable,
  share,
  Subject,
  take,
  tap,
  timeout,
} from 'rxjs';
import {ICqrsHttpService, ICqrsRoute} from '../models/cqrs-routing';

// Define a more specific action creator type for our SSE actions
export type MessageEventCreatorFactory = <T extends CqrsEvent>(eventType?: string) => EventCreator<string, T>;

// Injection token for SSE action type
export const SSE_EVENT_FACTORY = new InjectionToken<MessageEventCreatorFactory>('SSE_EVENT_FACTORY');

export const provideGlobalExecution = (config: {sseEventFactory: MessageEventCreatorFactory}) =>
  makeEnvironmentProviders([{provide: SSE_EVENT_FACTORY, useValue: config.sseEventFactory}]);

export const provideGlobalExecutionRoutes = (
  routes: {
    commandTypes: Record<string, string> | string[];
    queryTypes: Record<string, string> | string[];
    httpService: Type<ICqrsHttpService>;
  }[]
): EnvironmentProviders =>
  makeEnvironmentProviders([
    provideEnvironmentInitializer(() =>
      inject(GlobalExecutionStore).registerRoutes(
        routes.map(({commandTypes, queryTypes, httpService}) => ({
          commandTypes,
          queryTypes,
          httpService: inject(httpService),
        }))
      )
    ),
  ]);

const DEFAULT_COMMAND_TIMEOUT = 60000; // 60 seconds
/**
 * Hard cap for a single command execution, including explicit per-execution
 * overrides — a caller-provided timeout must not leave an execution pending
 * without any bound.
 */
const MAX_EXECUTION_TIMEOUT = 120000; // 2 minutes
// Mirrors the command timeout for queries. Queries have no caller-facing
// result$, so a hung transport would otherwise leave the loading flag set
// forever. On timeout the shared error path clears loading and emits an error
// event.
const DEFAULT_QUERY_TIMEOUT = 5000; // 5 seconds

export interface CommandExecutionOptions {
  timeout?: number;
  successEvents?: string[];
  errorEvents?: string[];
  executionId?: string;
  sseEventFactory?: MessageEventCreatorFactory;
}

export interface CommandResult extends IMessage {
  isSuccess: boolean;
  isError: boolean;
}

export interface CommandExecutionResult {
  traceId: string;
  result$: Observable<CommandResult>;
}

const stateName = 'globalExecution';

interface IGlobalExecutionState {
  loadingExecutions: Record<string, boolean>;
  trackedExecutions: Record<string, CommandExecutionResult>;
}

const initialState: IGlobalExecutionState = {
  loadingExecutions: {},
  trackedExecutions: {},
};

export const GlobalExecutionStore = signalStore(
  {providedIn: 'root'},
  withDevTools(stateName, withGlitchTracking()),
  withState(initialState),
  withMethods(state => ({
    setLoadingExecution(traceId: string, isLoading: boolean): void {
      updateState(state, `[GE] execution ${isLoading ? 'started' : 'finished'}`, state => ({
        loadingExecutions: {
          ...state.loadingExecutions,
          [traceId]: isLoading,
        },
      }));
    },
    cleanupLoadingExecution(traceId: string): void {
      updateState(state, `[GE] cleanupLoadingExecution`, state => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {[traceId]: _, ...remaining} = state.loadingExecutions;
        return {loadingExecutions: remaining};
      });
    },
    setTrackedExecution(traceId: string, execution: CommandExecutionResult): void {
      updateState(state, `[GE] setTrackedExecution`, state => ({
        trackedExecutions: {
          ...state.trackedExecutions,
          [traceId]: execution,
        },
      }));
    },
    cleanupTrackedExecution(traceId: string): void {
      updateState(state, `[GE] cleanupTrackedExecution`, state => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {[traceId]: _, ...remaining} = state.trackedExecutions;
        return {trackedExecutions: remaining};
      });
    },
    cleanupAllExecutions(): void {
      updateState(state, `[GE] cleanupAllExecutions`, _ => ({loadingExecutions: {}, trackedExecutions: {}}));
    },
  })),
  withComputed(({loadingExecutions}) => ({
    hasActiveExecutions: computed(() => Object.values(loadingExecutions()).some(isLoading => isLoading)),
    activeExecutionCount: computed(() => Object.values(loadingExecutions()).filter(isLoading => isLoading).length),
  })),
  withMethods((_, events = inject(Events), destroyRef = inject(DestroyRef)) => {
    const commandRouteMap = new Map<string, ICqrsHttpService>();
    const queryRouteMap = new Map<string, ICqrsHttpService>();

    // The bridge: Lazy modules push events here, and withEventHandlers listens to it
    const dynamicCommandBus$ = new Subject<EventInstance<string, CqrsCommand>>();
    const dynamicQueryBus$ = new Subject<EventInstance<string, CqrsQuery>>();

    return {
      _getCommandEvents: () => dynamicCommandBus$.asObservable(),
      _getQueryEvents: () => dynamicQueryBus$.asObservable(),
      _getCommandRoute: (type: string) => commandRouteMap.get(type),
      _getQueryRoute: (type: string) => queryRouteMap.get(type),

      registerRoutes(routes: ICqrsRoute[]) {
        const newRegisteredCommandTypes: string[] = [];
        const newRegisteredQueryTypes: string[] = [];

        routes.forEach(({commandTypes: routeCommandTypes, queryTypes: routeQueryTypes, httpService}) => {
          const commandTypes = Array.isArray(routeCommandTypes) ? routeCommandTypes : Object.values(routeCommandTypes);
          const queryTypes = Array.isArray(routeQueryTypes) ? routeQueryTypes : Object.values(routeQueryTypes);

          commandTypes
            .filter(commandType => !commandRouteMap.has(commandType))
            .forEach(commandType => {
              commandRouteMap.set(commandType, httpService);
              newRegisteredCommandTypes.push(commandType);
            });
          queryTypes
            .filter(queryType => !queryRouteMap.has(queryType))
            .forEach(queryType => {
              queryRouteMap.set(queryType, httpService);
              newRegisteredQueryTypes.push(queryType);
            });
        });

        // Dynamically listen to the newly registered event types

        if (newRegisteredCommandTypes.length > 0) {
          events
            .on(...eventsForTypes(CqrsCommandFactory, newRegisteredCommandTypes))
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(event => dynamicCommandBus$.next(event));
        }

        if (newRegisteredQueryTypes.length > 0) {
          events
            .on(...eventsForTypes(CqrsQueryFactory, newRegisteredQueryTypes))
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(event => dynamicQueryBus$.next(event));
        }
      },
    };
  }),
  withEventHandlers(state => ({
    sendCqrsCommand$: state._getCommandEvents().pipe(
      tap((event: EventInstance<string, CqrsCommand>) => {
        // redux dev tools logging
        updateState(state, event, state => ({}));
      }),
      concatMap((event: EventInstance<string, CqrsCommand>) => {
        const commandType = event.payload.type;
        const cqrsHttpService = state._getCommandRoute(commandType);
        if (!cqrsHttpService) {
          return EMPTY;
        }
        // Commands through this store are async-by-default server-side: a 202
        // ack is neither success nor failure, so it produces no event — the
        // execution stays pending until its outcome arrives out-of-band on the
        // command's SSE trace subject. The command endpoint answers exactly
        // once, so take(1) completes the inner stream right after the ack and
        // concatMap can serialize the next command.
        return cqrsHttpService.sendCqrsCommand(event.payload).pipe(
          take(1),
          filter(({status}) => status !== 202),
          mapResponse({
            next: body => CqrsCommandSuccessFactory(commandType)(body),
            error: error => AppErrorEvent(CqrsCommandErrorType)(error),
          })
        );
      })
    ),
    sendCqrsQuery$: state._getQueryEvents().pipe(
      tap((event: EventInstance<string, CqrsQuery>) => {
        // redux dev tools logging
        updateState(state, event, state => ({}));
      }),
      mergeMap((event: EventInstance<string, CqrsQuery>) => {
        const queryType = event.payload.type;
        const cqrsHttpService = state._getQueryRoute(queryType);
        if (!cqrsHttpService) {
          return EMPTY;
        }
        return cqrsHttpService.sendCqrsQuery(event.payload).pipe(
          timeout(DEFAULT_QUERY_TIMEOUT),
          mapResponse({
            next: payload => {
              state.setLoadingExecution(event.payload.metadata.traceId, false);
              if (event.payload.metadata.queryContext) {
                return CqrsQuerySuccessWithContextFactory(queryType)({
                  response: payload,
                  queryContext: event.payload.metadata.queryContext as string,
                });
              }
              return CqrsQuerySuccessFactory(queryType)(payload);
            },
            error: error => {
              state.setLoadingExecution(event.payload.metadata.traceId, false);
              return AppErrorEvent(CqrsQueryErrorType)(error);
            },
          })
        );
      })
    ),
  })),
  withMethods(
    (
      state,
      sseEventFactory = inject(SSE_EVENT_FACTORY),
      events = inject(Events),
      dispatcher = inject(Dispatcher),
      errorService = inject(ErrorService)
    ) => ({
      executeCommand<TCommand extends CqrsCommand>(command: TCommand, options: CommandExecutionOptions) {
        const traceId = newId();

        state.setLoadingExecution(traceId, true);

        if (!state._getCommandRoute(command.type)) {
          console.error(
            `[CQRS] executeCommand called for unregistered command type: ${command.type}. ` +
              `Make sure to register it in provideGlobalExecutionRoutes.`
          );
          state.cleanupLoadingExecution(traceId);
        }

        const factory = options.sseEventFactory ?? sseEventFactory;
        const successEvents = options.successEvents ?? [];
        const errorEvents = options.errorEvents ?? [];
        // The async failure-envelope type is always listened for: failures of
        // acked commands arrive out-of-band on the command's trace subject
        // under this type, regardless of the caller's declared errorEvents.
        const outcomeEventTypes = [...new Set([...successEvents, ...errorEvents, COMMAND_EXECUTION_FAILED_EVENT_TYPE])];

        // The event-type subscription stays the trigger path, but the outcome is
        // correlated by the traceId in the SSE message metadata: two concurrent
        // same-type commands resolve independently instead of racing on take(1).
        // Messages without a traceId cannot be correlated and keep the legacy
        // type-match behavior.
        const events$ = events.on(...eventsForTypes(factory, outcomeEventTypes)).pipe(
          filter(event => {
            const eventTraceId = event.payload?.metadata?.traceId;
            return !eventTraceId || eventTraceId === traceId;
          })
        );

        const httpErrors$: Observable<{error: IErrorModel}> = errorService.errors$.pipe(
          filter(error => {
            const code = (error as {code?: number}).code;
            return Boolean(code === 0 || (code && code >= 400));
          }), // 4xx and 5xx errors
          filter(error => (error?.traceId || null) === traceId),
          map(error => ({error}))
        );

        const timeoutMs = Math.min(options?.timeout ?? DEFAULT_COMMAND_TIMEOUT, MAX_EXECUTION_TIMEOUT);

        const outcome$ = merge(events$, httpErrors$).pipe(
          take(1),
          timeout(timeoutMs),
          map(eventOrError => {
            // Check if this is an HTTP error event
            if ('error' in eventOrError) {
              // Create error result for HTTP errors
              return {
                type: 'HttpError',
                isSuccess: false,
                isError: true,
                metadata: {traceId},
              } as CommandResult;
            } else {
              // Handle SSE event normally
              const sseEvent = eventOrError.payload;
              const isErrorEvent =
                errorEvents.includes(sseEvent.type) || sseEvent.type === COMMAND_EXECUTION_FAILED_EVENT_TYPE;
              const isSuccessEvent = !isErrorEvent && successEvents.includes(sseEvent.type);

              if (isErrorEvent) {
                // Surface async failure envelopes to the toast layer using the
                // server-formatted message from the envelope's `error` field.
                errorService.emitError({text: getCommandFailureText(sseEvent.payload)});
              }

              return {
                ...sseEvent,
                isSuccess: isSuccessEvent,
                isError: isErrorEvent,
              } as CommandResult;
            }
          }),
          catchError(() => {
            // Auto-cleanup loading state on timeout
            state.cleanupLoadingExecution(traceId);
            errorService.emitError({text: 'Request timeout. Please try again.'});
            return EMPTY;
          })
        );

        // Single-emission outcome: loadingExecutions[traceId] stays true through
        // the 202 ack and is released only by the final outcome, the timeout
        // cleanup above, or cleanupAllExecutions.
        const result$ = outcome$.pipe(
          tap(() => state.cleanupLoadingExecution(traceId)),
          share() // Share the observable to prevent multiple timeout errors from multiple subscriptions
        );

        dispatcher.dispatch(CqrsCommandFactory<TCommand>(command.type)(command.withMetadata({traceId})));

        const execution: CommandExecutionResult = {traceId, result$};

        // If executionId provided, track this execution
        const executionId = options?.executionId;
        if (executionId !== undefined) {
          state.setTrackedExecution(executionId, execution);

          // Auto-cleanup tracked execution on completion (success, error, or unsubscription)
          result$.pipe(finalize(() => state.cleanupTrackedExecution(executionId))).subscribe();
        }

        return execution;
      },
      executeQuery<TQuery extends CqrsQuery>(query: TQuery, queryContext?: string): {traceId: string} {
        const traceId = newId();
        state.setLoadingExecution(traceId, true);
        query.withMetadata({traceId, ...(queryContext && {queryContext})});
        if (!state._getQueryRoute(query.type)) {
          console.error(
            `[CQRS] executeQuery called for unregistered query type: ${query.type}. ` +
              `Make sure to register it in provideGlobalExecutionRoutes.`
          );
          state.cleanupLoadingExecution(traceId);
        }
        dispatcher.dispatch(CqrsQueryFactory<TQuery>(query.type)(query));
        return {traceId};
      },
    })
  )
);
